import { useTranslation } from 'next-i18next'
import { makeStyles } from '@material-ui/core'
import { useRouter } from 'next/router'
import DomesticForm from '../checkprice/DomesticForm'
import InterForm from '../checkprice/InterForm'
import T, { specularRing, specularHost, specularRingReduced } from '../home-new/tokens'

// Checkprice-page ("?new") tabbed search card. Visually this is the same
// frosted-glass card + 3-tab treatment as components/home-new/CheckpriceFormNew.js
// (the /new hero form) — intentionally duplicated here rather than imported,
// because that component hardcodes submitMode="redirect" and doesn't forward
// setCheckpriceResultList/setResultStatus/setLastFetchBody. This page needs
// submitMode="fetch": DomesticForm/InterForm call onRouting(..., {shallow:true})
// and populate results in place via those setters, without a full page
// remount — a redirect-mode submit would remount CheckpriceNew on the new
// query and lose all local result state (see CheckpriceNew.js comments).
// Editing the shared CheckpriceFormNew.js was out of scope (it also backs the
// homepage hero), so this sibling file exists instead. Keep the two in sync
// visually if the design changes.
//
// The shared Domestic/Inter forms rewrite the URL from their own state via
// the global Context `onRouting` — NEW is the default design now, so no
// local override is needed to stay in the new chrome.

const TABS = [
  {
    key: 'domestic',
    labelKey: 'checkpriceForm.tabDomestic',
    icon: '/static/img/home/checkprice-icon-truck.svg',
  },
  {
    key: 'inter',
    labelKey: 'checkpriceForm.tabInter',
    icon: '/static/img/home/checkprice-icon-plane.svg',
  },
  {
    key: 'track',
    labelKey: 'checkpriceForm.tabTrack',
    icon: null, // inline SVG below
  },
]

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
  },
  tabRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '6px',
    paddingLeft: 0,
    marginBottom: -2,
    position: 'relative',
    zIndex: 2,
    [theme.breakpoints.down('xs')]: {
      gap: '3px',
      paddingLeft: 0,
      justifyContent: 'space-between',
    },
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '17px 32px',
    border: 'none',
    borderRadius: '16px 16px 0 0',
    cursor: 'pointer',
    fontFamily: T.font.heading,
    fontSize: 19,
    fontWeight: 500,
    lineHeight: 1,
    background: 'rgba(255, 255, 255, 0.75)',
    color: T.color.muted,
    transition: 'background 0.25s ease, color 0.25s ease',
    touchAction: 'manipulation',
    '& img, & svg': {
      width: 30,
      height: 30,
      objectFit: 'contain',
      flexShrink: 0,
    },
    '&:hover': {
      background: 'rgba(255, 255, 255, 1)',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      gap: '4px',
      padding: '10px 6px',
      fontSize: 12,
      flex: 1,
      textAlign: 'center',
      '& img, & svg': { width: 22, height: 22 },
    },
  },
  tabActive: {
    background: T.glass.panel,
    backdropFilter: T.glass.blur,
    WebkitBackdropFilter: T.glass.blur,
    color: T.color.ink,
    boxShadow: 'none',
    '&:hover': {
      background: T.glass.panel,
    },
  },
  card: {
    background: T.glass.panel,
    backdropFilter: T.glass.blur,
    WebkitBackdropFilter: T.glass.blur,
    borderRadius: '0 28px 28px 28px',
    boxShadow: T.shadow.lg,
    padding: '26px 40px 30px',
    position: 'relative',
    zIndex: 1,
    [theme.breakpoints.down('xs')]: {
      padding: '18px 16px 22px',
      borderRadius: '0 0 18px 18px',
    },
    '& .MuiGrid-item': {
      paddingBottom: '12px !important',
    },
    '& h3': {
      display: 'none',
    },
    '& input, & textarea, & select, & label, & button, & .MuiInputBase-root, & .MuiInputBase-input, & .MuiFormLabel-root, & .MuiButton-label, & .MuiTypography-root, & p, & a': {
      fontFamily: `${T.font.body} !important`,
    },
    '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    '& input, & .MuiInputBase-input, & .MuiSelect-select': {
      touchAction: 'manipulation',
    },
    '& button[type="submit"]': {
      ...specularHost,
      borderRadius: '999px',
      minHeight: 50,
      fontSize: '16px',
      overflow: 'hidden',
      background: `${T.gradient.blue} !important`,
      '&:hover, &:focus': {
        background: `${T.gradient.blue} !important`,
        filter: 'brightness(1.06)',
      },
      '&::before': specularRing(T.ring.blue),
      '@media (prefers-reduced-motion: reduce)': {
        ...specularRingReduced,
      },
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#008EFF',
      borderWidth: '1.5px !important',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0072D6',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#008EFF',
      borderWidth: '2px !important',
    },
    '& .MuiSelect-icon': {
      color: '#008EFF',
    },
    // 48×48 hit area for the origin/destination dropdown arrow (Lighthouse
    // tap-targets). Center the endAdornment and nudge the button right so the
    // arrow lands on the same right column as the box-size Select icon (the
    // enlarged 48px button otherwise sits ~10px low and ~12px too far left).
    '& .MuiAutocomplete-popupIndicator': {
      width: 48,
      height: 48,
      position: 'relative',
      right: -12,
    },
    // No circular IconButton hover tint on the arrow (matches the box-size
    // Select icon, which has none).
    '& .MuiAutocomplete-popupIndicator:hover': {
      backgroundColor: 'transparent',
    },
    '& .MuiAutocomplete-endAdornment': {
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root': {
      color: '#008EFF',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 27,
    fontWeight: 600,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 18px',
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
      margin: '0 0 14px',
    },
  },
  panel: {
    '&[hidden]': {
      display: 'none',
    },
  },
  trackForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: 560,
    margin: '0 auto',
  },
  trackInput: {
    width: '100%',
    height: 52,
    padding: '0 18px',
    fontSize: 16,
    fontFamily: T.font.body,
    color: T.color.ink,
    background: '#fff',
    border: `1.5px solid ${T.color.line}`,
    borderRadius: 12,
    outline: 'none',
    touchAction: 'manipulation',
    transition: 'border-color 0.2s ease',
    '&:focus': {
      borderColor: T.color.brand,
    },
    '&::placeholder': {
      color: T.color.faint,
    },
  },
  trackBtn: {
    ...specularHost,
    height: 52,
    border: 'none',
    borderRadius: 999,
    background: T.gradient.blue,
    color: T.color.onBrand,
    fontFamily: T.font.heading,
    fontSize: 17,
    fontWeight: 500,
    cursor: 'pointer',
    touchAction: 'manipulation',
    transition: 'filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
    '&::before': specularRing(T.ring.blue),
    '&:hover': {
      filter: 'brightness(1.05)',
      transform: 'translateY(-2px)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      ...specularRingReduced,
    },
  },
}))

const TrackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2.5 3.5 6.75v10.5L12 21.5l8.5-4.25V6.75L12 2.5Z"
      stroke="#FF7A1A"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M3.7 6.9 12 11l8.3-4.1M12 11v10.3"
      stroke="#FF7A1A"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
)

const CheckpriceHeroFormNew = ({
  activeTab,
  setActiveTab,
  setCheckpriceResultList,
  setResultStatus,
  setLastFetchBody,
}) => {
  const classes = useStyles()
  const { t } = useTranslation('checkprice')
  const Router = useRouter()

  const submitTrack = (e) => {
    e.preventDefault()
    const form = e.target
    const code = form.trackNo.value.trim()
    if (!code) return
    Router.push(`/tracking?tracking_code=${encodeURIComponent(code)}`)
  }

  const headingText =
    activeTab === 2
      ? t('checkpriceForm.headingTrack')
      : t('checkpriceForm.headingPrice')

  return (
    <div className={classes.wrapper}>
      <div className={classes.tabRow} role="tablist">
        {TABS.map((tab, i) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === i}
            className={`${classes.tab} ${
              activeTab === i ? classes.tabActive : ''
            }`}
            onClick={() => setActiveTab(i)}
            type="button"
          >
            {tab.icon ? (
              <img
                src={tab.icon}
                alt=""
                aria-hidden="true"
                width={30}
                height={30}
                decoding="async"
              />
            ) : (
              <TrackIcon />
            )}
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div className={classes.card}>
        <h2 className={classes.heading} key={headingText}>
          {headingText}
        </h2>

        <div className={classes.panel} hidden={activeTab !== 0}>
          <DomesticForm
            submitMode="fetch"
            setCheckpriceResultList={setCheckpriceResultList}
            setResultStatus={setResultStatus}
            setLastFetchBody={setLastFetchBody}
            key="domesticForm"
          />
        </div>
        <div className={classes.panel} hidden={activeTab !== 1}>
          <InterForm
            submitMode="fetch"
            setCheckpriceResultList={setCheckpriceResultList}
            setResultStatus={setResultStatus}
            key="interForm"
          />
        </div>
        <div className={classes.panel} hidden={activeTab !== 2}>
          <form className={classes.trackForm} onSubmit={submitTrack}>
            <input
              className={classes.trackInput}
              type="text"
              name="trackNo"
              placeholder={t('checkpriceForm.trackPlaceholder')}
              aria-label={t('checkpriceForm.trackAria')}
            />
            <button className={classes.trackBtn} type="submit">
              {t('checkpriceForm.trackButton')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CheckpriceHeroFormNew
